/**
 * WordPress Onboarding API - save and retrieve onboarding step statuses
 * for WordPress merchants.
 *
 * Endpoints:
 *   GET  /bot/api/wordpress-onboarding  → retrieve step statuses
 *   POST /bot/api/wordpress-onboarding  → update step statuses
 */

import { getBaseUrl, buildUrl } from './utils.js';
import { getAuthHeaders } from './auth';

/**
 * Mapping between step IDs and API field names.
 * Maps to ONBOARDING_FIELDS: plugin, sync, settings, widget, testai, activate
 */
const STEP_ID_TO_API_FIELD = {
  1: 'plugin',
  2: 'sync',
  3: 'settings',
  4: 'widget',
  5: 'testai',
  6: 'activate',
};

/**
 * Default step definitions for WordPress onboarding.
 */
const createDefaultSteps = () => [
  { id: 1, title: 'Install & Connect Plugin',  description: 'Copy your API key, download the AeroChat plugin, and install it on your WordPress site.',         status: 'pending', type: 'required',    action: null,                capabilityLine: 'Once connected, your WordPress content will automatically sync to AeroChat.' },
  { id: 2, title: 'Review Synced Content',      description: 'Check that your WordPress pages, posts and products have been imported to your knowledge base.',  status: 'pending', type: 'required',    action: 'View Knowledge Base', capabilityLine: 'Your AI will learn from your synced WordPress content to answer customer questions.' },
  { id: 3, title: 'Review Business Settings',   description: 'Set your business name, timezone, language and AI behaviour to personalise your assistant.',      status: 'pending', type: 'required',    action: 'Open Settings',       capabilityLine: 'Personalised responses based on your business identity and operating hours.' },
  { id: 4, title: 'Customize Chat Widget',      description: 'Add your brand colours, logo and welcome message to make the widget yours.',                     status: 'pending', type: 'recommended', action: 'Customise Widget',    capabilityLine: 'A branded widget builds customer trust and matches your site experience.' },
  { id: 5, title: 'Test Drive Your AI',         description: 'Send a test message and see how your AI responds before going live.',                            status: 'pending', type: 'required',    action: 'Test Now',            capabilityLine: 'Preview exactly how your AI assistant will respond to real customer questions.' },
  { id: 6, title: 'Activate Chat Widget',       description: 'Confirm the AeroChat chat widget is live and active on your WordPress site.',                    status: 'pending', type: 'required',    action: 'Verify Now',          capabilityLine: 'Ensures everything is working before your customers start chatting.' },
];

/**
 * Fetch all WordPress onboarding step statuses.
 * GET /bot/api/wordpress-onboarding
 *
 * @returns {Promise<Array>} array of step objects with statuses from backend
 */
export async function getWordpressOnboardingSteps() {
  const base = getBaseUrl();
  const url = buildUrl(base, 'bot/api/wordpress-onboarding');

  let apiData = null;
  try {
    const res = await fetch(`${url}?_t=${Date.now()}`, {
      method: 'GET',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache', ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    apiData = await res.json();
    if (apiData.error) throw new Error(apiData.error);
  } catch (err) {
    console.warn('WordPress onboarding API failed, using defaults:', err);
  }

  const steps = createDefaultSteps();
  if (!apiData) return steps;

  return steps.map((step) => {
    const apiField = STEP_ID_TO_API_FIELD[step.id];
    if (apiField && apiData[apiField] !== undefined) {
      const val = apiData[apiField];
      if (val === 1) return { ...step, status: 'completed' };
      if (val === 2) return { ...step, status: 'visited' };
      if (val === 3) return { ...step, status: 'skipped' };
    }
    return step;
  });
}

/**
 * Update the status of a single onboarding step.
 * POST /bot/api/wordpress-onboarding
 *
 * @param {number} stepId - step ID (1-6)
 * @param {string} status - 'pending' | 'visited' | 'completed' | 'skipped'
 * @returns {Promise<object|null>}
 */
/**
 * Check whether the WordPress plugin has connected to the backend (i.e.
 * the merchant pasted the API key and the plugin pinged ``/bot/wordpress/
 * webhook/ping``). Used by the onboarding "Activate Now" step to avoid
 * marking activation complete when the plugin hasn't actually phoned home.
 *
 * GET /bot/wordpress/connection-status
 *
 * @returns {Promise<{connected: boolean, widgetEnabled: boolean, siteUrl: string|null, connectedAt: string|null}>}
 */
export async function fetchWordpressConnectionStatus() {
  try {
    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/wordpress/connection-status');
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache', ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error(`API returned ${res.status}`);
    const data = await res.json();
    return {
      connected: !!data.connected,
      widgetEnabled: !!data.widget_enabled,
      siteUrl: data.site_url || null,
      connectedAt: data.connected_at || null,
    };
  } catch (err) {
    console.warn('Failed to fetch WordPress connection status:', err);
    return { connected: false, widgetEnabled: false, siteUrl: null, connectedAt: null };
  }
}

export async function updateWordpressOnboardingStep(stepId, status) {
  try {
    const apiField = STEP_ID_TO_API_FIELD[stepId];
    if (!apiField) {
      console.warn(`Step ${stepId} does not map to API field`);
      return null;
    }

    let apiValue = 0;
    if (status === 'completed') apiValue = 1;
    else if (status === 'visited') apiValue = 2;
    else if (status === 'skipped') apiValue = 3;

    const base = getBaseUrl();
    const url = buildUrl(base, 'bot/api/wordpress-onboarding');

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ [apiField]: apiValue }),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `API returned ${res.status}`);
    }

    return await res.json();
  } catch (err) {
    console.warn('Failed to save WordPress onboarding step:', err);
    return null;
  }
}
